import type { FC, PropsWithChildren } from 'react';
import PropTypes from 'prop-types';
import { isHydrated } from '../../../utils/utils';
import { GetIcon } from '../../icons';

type WithClassNameProps = PropsWithChildren<{
    className?: string;
    isThumbnail?: boolean;
    isTall?: boolean;
    dataTestId?: string;
}>;

const PlayCircleOverlay: FC<Omit<WithClassNameProps, 'isTall'>> = ({
    className,
    children,
    isThumbnail,
}) => (
    <>
        <div
            data-testid="photo-grid-video-asset-play-container"
            className={`absolute inset-0 flex items-center justify-center ${className}`}
        >
            <GetIcon name="play-in-circle" size={isThumbnail ? 'lg' : 'xl'} />
        </div>
        {children}
    </>
);

PlayCircleOverlay.propTypes = {
    className: PropTypes.string,
    isThumbnail: PropTypes.bool,
};

PlayCircleOverlay.defaultProps = {
    className: '',
    isThumbnail: false,
};

export const AspectRatioContainer: FC<Omit<WithClassNameProps, 'isThumbnail' | 'className'>> = ({
    children,
    isTall,
    dataTestId,
}) => {
    const isAspectRatioSupported = isHydrated() ? CSS.supports('aspect-ratio: 1/1') : true;

    if (isAspectRatioSupported) {
        return (
            <div
                className={`relative flex aspect-square w-full ${isTall ? 'md:aspect-[9/16]' : ''}`}
                data-testid={dataTestId ?? 'photo-grid-asset-container'}
            >
                <div className="h-full w-full">{children}</div>
            </div>
        );
    }

    return (
        <div
            className={`relative h-full w-full before:block before:w-full before:pt-[100%] before:content-[''] ${
                isTall ? 'md:before:pt-[177.78%]' : ''
            }`}
            data-testid={dataTestId ?? 'photo-grid-asset-container-legacy'}
        >
            <div className="absolute inset-0">
                <div className="h-full w-full">{children}</div>
            </div>
        </div>
    );
};

AspectRatioContainer.propTypes = {
    isTall: PropTypes.bool,
};

AspectRatioContainer.defaultProps = {
    isTall: false,
};

export type AssetContainerProps = PropsWithChildren<{
    isThumbnail?: boolean;
}>;

export const YouTubeContainer: FC<AssetContainerProps> = ({ children, isThumbnail }) => (
    <AspectRatioContainer dataTestId="photo-grid-youtube-video-asset-container">
        <PlayCircleOverlay className="absolute inset-0" isThumbnail={isThumbnail}>
            {children}
        </PlayCircleOverlay>
    </AspectRatioContainer>
);

YouTubeContainer.defaultProps = {
    isThumbnail: false,
};

export const BrandSpecialistVideoContainer: FC<AssetContainerProps> = ({
    children,
    isThumbnail,
}) => (
    <AspectRatioContainer
        isTall={!isThumbnail}
        dataTestId="photo-grid-brand-specialist-video-asset-container"
    >
        <PlayCircleOverlay className="absolute inset-0" isThumbnail={isThumbnail}>
            {children}
        </PlayCircleOverlay>
    </AspectRatioContainer>
);

BrandSpecialistVideoContainer.defaultProps = {
    isThumbnail: false,
};

export const ImageContainer: FC<Omit<AssetContainerProps, 'isThumbnail'>> = ({ children }) => (
    <AspectRatioContainer dataTestId="photo-grid-image-asset-container">
        <div className="flex h-full w-full items-center justify-center">{children}</div>
    </AspectRatioContainer>
);
